import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { StatCard } from "@/components/common";
import { usd, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { UserPlus, Clock, Send, FileCheck, Home, DollarSign, TrendingUp, Wallet, AlertTriangle, Phone, Activity, Upload, Sparkles } from "lucide-react";

const SAMPLE_FILINGS = `9021 Sunset Ridge, Henderson, NV, 89052, Clark, Maria Delgado, Notice of Sale, 2026-08-08, 32000, Nevada Trustee LLC
77 Larkspur Ln, Glendale, CA, 91205, Los Angeles, Robert Kim, Notice of Default, 2026-08-14, 21500, Pacific Trustee Services
2210 Camelback Rd, Phoenix, AZ, 85016, Maricopa, Angela Ross, Notice of Sale, 2026-08-19, 44000, Desert Default Services`;

function ForeclosureImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const filings = text.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
      const [property_address, city, state, zip_code, county, owner_name, status, auction_date, reinstatement_amount, trustee_name] = line.split(",").map((s) => (s || "").trim());
      return { property_address, city, state, zip_code, county, owner_name, status, auction_date, reinstatement_amount: reinstatement_amount ? Number(reinstatement_amount) : null, trustee_name };
    }).filter((f) => f.property_address);
    if (!filings.length) return toast.error("Paste at least one filing row");
    setBusy(true);
    try {
      const { data } = await api.post("/foreclosures/import", { filings });
      toast.success(`Imported ${data.foreclosures_created} filings · ${data.created_leads} new leads, ${data.matched_leads} matched`);
      setOpen(false); setText(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="import-foreclosures-btn"><Upload className="w-3.5 h-3.5 mr-1" /> Import Filings</Button></DialogTrigger>
      <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle className="font-display">Import Foreclosure Filings</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Paste a county foreclosure export (one filing per line, comma-separated):<br /><span className="font-mono-nums text-xs">address, city, state, zip, county, owner, status, auction_date (YYYY-MM-DD), reinstatement, trustee</span></p>
          <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder={SAMPLE_FILINGS} data-testid="foreclosure-import-text" className="font-mono-nums text-xs" />
          <Button variant="ghost" size="sm" onClick={() => setText(SAMPLE_FILINGS)} data-testid="load-sample-filings">Load sample filings</Button>
        </div>
        <DialogFooter><Button onClick={run} disabled={busy} data-testid="foreclosure-import-submit">{busy ? "Importing…" : "Import & Create Leads"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Panel({ title, children, testid, action }) {
  return (
    <div className="bg-card border border-border rounded-md" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h3 className="font-display font-bold tracking-tight">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events?scope=upcoming").then(({ data }) => setEvents(data.events || [])).catch(() => {}); }, []);
  const cd = (iso) => {
    const diff = new Date(iso) - new Date();
    if (diff < 0) return "past";
    const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000);
    return d > 0 ? `${d}d ${h}h` : `${h}h ${Math.floor((diff % 3600000) / 60000)}m`;
  };
  const styles = { closing: "text-emerald-600", meeting: "text-blue-600", investor: "text-purple-600", custom: "text-slate-500" };
  const labels = { closing: "Closing", meeting: "Meeting", investor: "Investor", custom: "Event" };
  const upcoming = events.filter((e) => new Date(e.start_at) >= new Date()).slice(0, 6);
  return (
    <Panel title="Upcoming Schedule" testid="panel-schedule" action={<Link to="/schedule" className="text-xs text-accent hover:underline">Open schedule</Link>}>
      {upcoming.length === 0 ? <Empty text="No upcoming events" /> :
        <ul className="space-y-3" data-testid="dashboard-events">
          {upcoming.map((e) => {
            const row = (
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="font-medium truncate">{e.title}</div>
                  <div className="text-xs"><span className={`font-semibold ${styles[e.event_type] || ""}`}>{labels[e.event_type]}</span> · {fmtDate(e.start_at)}</div>
                </div>
                <span className="text-xs font-bold tabnum shrink-0 text-accent">{cd(e.start_at)}</span>
              </div>
            );
            return <li key={e.id} data-testid={`dashboard-event-${e.id}`}>{e.lead_id ? <Link to={`/leads/${e.lead_id}`} className="block hover:opacity-80">{row}</Link> : row}</li>;
          })}
        </ul>}
    </Panel>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const canBrief = !["Read Only", "Vendor", "Capital Partner"].includes(user?.role);
  const [d, setD] = useState(null);
  const load = () => api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-8 text-muted-foreground">Loading dashboard…</div>;
  const k = d.kpis;

  const cards = [
    { label: "New Leads", value: k.new_leads, icon: UserPlus },
    { label: "Follow-Ups Due", value: k.follow_ups_due, icon: Clock },
    { label: "Offers Submitted", value: k.offers_submitted, icon: Send },
    { label: "Under Contract", value: k.under_contract, icon: FileCheck },
    { label: "Closings This Month", value: k.closings_this_month, icon: Home },
    { label: "Pipeline Value", value: usd(k.pipeline_value), icon: DollarSign },
    { label: "Projected Equity", value: usd(k.projected_equity), icon: TrendingUp },
    { label: "Projected Net Profit", value: usd(k.projected_net_profit), icon: Wallet },
  ];

  return (
    <div>
      <PageHeader title="Executive Dashboard" subtitle="Pipeline health across the acquisition lifecycle" />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {canBrief && <AiBriefing />}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>

        <UpcomingEvents />

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Panel title="Acquisition Pipeline by Status" testid="panel-pipeline">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={d.pipeline} margin={{ left: -20 }}>
                  <XAxis dataKey="status" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={70} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {d.pipeline.map((e, i) => <Cell key={e.stage || e.label || e.name || `bar-${i}`} fill={i % 2 ? "hsl(var(--accent))" : "hsl(var(--primary))"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <Panel title="Foreclosure Deadlines" testid="panel-foreclosure"
            action={<div className="flex items-center gap-2">{d.foreclosures_urgent > 0 && <span className="text-[11px] font-bold uppercase tracking-wider bg-destructive/10 text-destructive border border-destructive/30 rounded-full px-2.5 py-1" data-testid="foreclosure-urgent-badge">{d.foreclosures_urgent} within 30 days</span>}<ForeclosureImport onDone={load} /></div>}>
            {d.foreclosure_deadlines.length === 0 ? <Empty text="No active foreclosure deadlines" /> :
              <ul className="space-y-2">
                {d.foreclosure_deadlines.map((f) => {
                  const urgent = f.within_30_days;
                  const body = (
                    <div className={`flex items-start gap-3 text-sm rounded-md px-2 py-2 -mx-2 ${urgent ? "bg-destructive/5 border border-destructive/20" : ""}`}>
                      <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${urgent ? "text-destructive" : "text-muted-foreground"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{f.property_address || f.status}</div>
                        <div className="text-muted-foreground text-xs">{f.status} · Auction {fmtDate(f.auction_date)} · Reinstatement {usd(f.reinstatement_amount)}</div>
                      </div>
                      {f.days_until_auction != null && (
                        <span className={`text-xs font-bold shrink-0 tabnum ${urgent ? "text-destructive" : "text-muted-foreground"}`}>
                          {f.days_until_auction < 0 ? "past" : `${f.days_until_auction}d`}
                        </span>
                      )}
                    </div>
                  );
                  return <li key={f.id} data-testid={`foreclosure-item-${f.id}`}>{f.lead_id ? <Link to={`/leads/${f.lead_id}`} className="block hover:opacity-80 transition-opacity">{body}</Link> : body}</li>;
                })}
              </ul>}
          </Panel>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Panel title="Upcoming Follow-Ups" testid="panel-followups" action={<Link to="/tasks" className="text-xs text-accent hover:underline">View all</Link>}>
            {d.upcoming_follow_ups.length === 0 ? <Empty text="Nothing due" /> :
              <ul className="space-y-3">
                {d.upcoming_follow_ups.map((t) => (
                  <li key={t.id} className="flex items-center justify-between text-sm">
                    <span className="truncate">{t.task_name}</span>
                    <span className="text-muted-foreground text-xs shrink-0 ml-3">{fmtDate(t.due_date)}</span>
                  </li>
                ))}
              </ul>}
          </Panel>

          <Panel title="Offers Awaiting Response" testid="panel-offers">
            {d.offers_awaiting.length === 0 ? <Empty text="No pending offers" /> :
              <ul className="space-y-3">
                {d.offers_awaiting.map((o) => (
                  <li key={o.id} className="flex items-center justify-between text-sm">
                    <span>{o.offer_type || "Offer"}</span>
                    <span className="font-mono-nums">{usd(o.offer_amount)}</span>
                  </li>
                ))}
              </ul>}
          </Panel>

          <Panel title="Recent Communications" testid="panel-comms">
            {d.recent_communications.length === 0 ? <Empty text="No communications logged" /> :
              <ul className="space-y-3">
                {d.recent_communications.map((c) => (
                  <li key={c.id} className="flex items-start gap-3 text-sm">
                    <Phone className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                    <div><div className="font-medium">{c.communication_type} · {c.direction}</div><div className="text-muted-foreground text-xs truncate">{c.summary}</div></div>
                  </li>
                ))}
              </ul>}
          </Panel>
        </div>

        <Panel title="Team Activity" testid="panel-team">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {d.team_activity.map((m, i) => (
              <div key={m.id || m.email || m.name || `member-${i}`} className="flex items-center gap-3 p-3 rounded-md bg-secondary">
                <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-sm">{(m.name || "?")[0]}</div>
                <div className="min-w-0"><div className="text-sm font-medium truncate">{m.name}</div><div className="text-xs text-muted-foreground truncate">{m.role}</div></div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Activity className="w-4 h-4" /> {text}</div>;
}

function AiBriefing() {
  return (
    <div className="bg-card border border-border rounded-md" data-testid="ai-briefing-panel">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <span className="font-display font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-muted-foreground" /> Daily Pipeline Briefing</span>
      </div>
      <div className="p-5">
        <p className="text-sm text-muted-foreground" data-testid="ai-briefing-disabled">Dashboard AI Briefing is not enabled in this release.</p>
      </div>
    </div>
  );
}
